import useSWR from 'swr';
import { swrFetcher } from 'contexts/user/index';
import { useEffect, useState } from 'react';
import { TeamResponse } from 'lib/teams';
import { Billing, SeatAllocations } from 'types/profile';
import { formatDateString } from 'lib/dateUtils';
import { planNameFromReference } from 'lib/plans';
import { capitalize } from 'lib/utils';

enum PlanStatus {
  Active = 'ACTIVE',
  Suspended = 'SUSPENDED',
  FailedPayment = 'FAILED_PAYMENT',
  Pending = 'PENDING',
  NoSubscription = 'NO_SUBSCRIPTION',
}

type Subscription = {
  plan: string;
  cycle: string;
} | null;

const usePlanStatus = (teamId: number) => {
  const [plan, setPlan] = useState<string>();
  const [nextSubscription, setNextSubscription] = useState<Subscription>();
  const [isStudio, setStudio] = useState<boolean>(false);
  const [planStatus, setPlanStatus] = useState<PlanStatus>(PlanStatus.Active);
  const [isActive, setActive] = useState<boolean>(false);
  const [isSuspended, setSuspended] = useState<boolean>(false);
  const [isCancelled, setCancelled] = useState<boolean>(false);
  const [canChangePlan, setCanChangePlan] = useState<boolean>(false);
  const [canCancelPlan, setCanCancelPlan] = useState<boolean>(false);
  const [canManageBilling, setCanManageBilling] = useState<boolean>(false);
  const [failedPayment, setFailedPayment] = useState<boolean>(false);
  const [displayLabel, setDisplayLabel] = useState<string>();
  const [formattedDue, setFormattedDue] = useState<string>();
  const [formattedCard, setFormattedCard] = useState<string>();
  const [isInvitable, setInvitable] = useState<boolean>(false);
  const [cycleLabel, setCycleLabel] = useState<string>();
  const [billLabel, setBillLabel] = useState<string>();
  const [creditLabel, setCreditLabel] = useState<string>();
  const [hasLoaded, setLoaded] = useState<boolean>(false);
  const [seats, setSeats] = useState<SeatAllocations>({
    available: {
      paid: 0,
    },
    used: {
      paid: 0,
      free: 0,
      viewer: 0,
    },
  });

  const { data: billing }: { data?: Billing } = useSWR(
    `/api/teams/${teamId}/billing`,
    swrFetcher
  );
  // if the pro plan is allowed, its single user
  // if its no longer available its multi user
  const invitablePlans = new Set([
    'ENTERPRISE',
    'NORMAL',
    'TEAM',
    'CUSTOM',
    'FREE',
    'PRO',
    null,
    undefined,
  ]);

  useEffect(() => {
    if (billing) {
      // set defaults
      setCanChangePlan(true);
      setCanCancelPlan(true);
      setActive(true);
      setSuspended(false);
      setFailedPayment(false);
      if (billing.data.next) {
        setNextSubscription({
          plan: planNameFromReference(billing.data.next.plan) ?? 'free',
          cycle: capitalize(billing.data.next.cycle?.toLowerCase()),
        });
      } else {
        setNextSubscription(null);
      }

      setPlan(planNameFromReference(billing.data.plan ?? 'free'));
      setStudio(billing.data.plan === 'NORMAL');
      setInvitable(invitablePlans.has(billing.data.plan));
      // right now we are saying that custom teams need to contact us to manage their plan.
      setCanChangePlan(
        billing.data.plan !== 'CUSTOM' &&
          billing.data.plan !== 'ENTERPRISE' &&
          billing.data.plan !== 'NORMAL'
      );
      setCanCancelPlan(
        billing.data.plan !== 'CUSTOM' && billing.data.plan !== 'ENTERPRISE'
      );
      setCanManageBilling(billing.data.paymentMethod !== 'invoice');
      setPlanStatus(billing.data.status as PlanStatus);
      switch (billing.data.status) {
        case PlanStatus.Active:
          setDisplayLabel('Active');
          break;
        case PlanStatus.Suspended:
          setDisplayLabel('Suspended');
          setActive(false);
          setSuspended(true);
          break;
        case PlanStatus.FailedPayment:
          setDisplayLabel('Failed Payment');
          setActive(false);
          setFailedPayment(true);
          break;
        case PlanStatus.Pending:
          setDisplayLabel('Pending - see Manage Billing');
          setActive(false);
          setFailedPayment(true);
          setCanChangePlan(false);
          break;
        case PlanStatus.NoSubscription:
          setDisplayLabel('Active');
          break;
      }

      const due = billing.data.nextDue
        ? formatDateString(billing.data.nextDue)
        : 'N/A';
      setFormattedDue(due);

      const { brand, last4 } = billing.data;

      [brand, last4].includes(null) || [brand, last4].includes(undefined)
        ? setFormattedCard('No payment method assigned')
        : setFormattedCard(`${brand} ending ${last4}`);

      setCycleLabel(capitalize(billing.data.cycle?.toLowerCase() ?? 'N/A'));
      setBillLabel(dollarValue(billing.data.nextBill ?? 0));

      if (billing.data.balance < 0) {
        setCreditLabel(dollarValue(billing.data.balance, true));
      }

      setCancelled(billing.data.isCanceled);
      if (billing.data.seats) setSeats(billing.data.seats);

      setLoaded(true);
    }
  }, [billing]);

  const dollarValue = (
    centValue: number,
    hideNegative: boolean = false
  ): string =>
    centValue < 0
      ? `${hideNegative ? '' : '-'}$${Math.abs(centValue / 100)}`
      : `$${centValue / 100}`;

  return {
    plan,
    isStudio,
    planStatus,
    isActive,
    isCancelled,
    isSuspended,
    failedPayment,
    displayLabel,
    formattedDue,
    formattedCard,
    isInvitable,
    cycleLabel,
    billLabel,
    creditLabel,
    hasLoaded,
    canChangePlan,
    canCancelPlan,
    canManageBilling,
    nextSubscription,
    seats,
  };
};

export default usePlanStatus;

export const usePlanStatusFromResponse = (team: TeamResponse) => {
  const [planStatus, setPlanStatus] = useState<PlanStatus>(PlanStatus.Active);
  const [isActive, setActive] = useState<boolean>(false);
  const [isCancelled, setCancelled] = useState<boolean>(false);
  const [displayLabel, setDisplayLabel] = useState<string>();
  const [formattedDue, setFormattedDue] = useState<string>();

  useEffect(() => {
    setPlanStatus(team.status as PlanStatus);

    switch (team.status) {
      case PlanStatus.Active:
        setDisplayLabel('Active');
        setActive(true);
        break;
      case PlanStatus.Suspended:
        setDisplayLabel('Suspended');
        break;
      case PlanStatus.FailedPayment:
        setDisplayLabel('Failed Payment');
        break;
      case PlanStatus.Pending:
        setDisplayLabel('Pending');
        break;
      case PlanStatus.NoSubscription:
        setDisplayLabel('Active');
        setActive(true);
        break;
    }

    const due = formatDateString(team.planExpirationDate);
    setFormattedDue(due);

    // we have a plan & its not set for renew
    setCancelled(team.planCode !== null && !team.renewSubscription);
  }, [team]);

  return {
    planStatus,
    isActive,
    isCancelled,
    displayLabel,
    formattedDue,
  };
};
